import { WeblabedFeatures } from 'src/commons/constants';

/**
 * Reads weblab-backed feature flags from `window.__StormConfig__.flags`, emitted
 * inline in the server response by AACChromeCommon's
 * `FrontendBundleHelper.composeGlobalStormConfig(PageContext)`.
 *
 * Why this exists alongside `window.__AACChrome__.context`:
 *
 * - It is available synchronously on the first render. `__AACChrome__.context`
 *   is populated asynchronously, so consumers there must poll and accept a
 *   post-hydration flip. This global is already in the HTML, so a server-
 *   rendered app can read it during its first client render and agree with the
 *   markup it hydrates.
 * - Flat lookup instead of scanning `featureLaunchInfo.features`.
 *
 * Both surfaces carry the same values, derived from one set of server-side
 * WeblabCheckProvider calls. Keys are the `FeatureLaunchInfo.Feature` constant
 * names, which are exactly the `WeblabedFeatures` values.
 *
 * Two properties worth knowing:
 *
 * - Shadow DOM safe. Shadow roots encapsulate the DOM tree and CSS, not the
 *   JavaScript realm, so a micro-frontend inside a shadow root reads this like
 *   any other consumer. Nothing here touches `document`.
 * - Resolved through `globalThis`, not `window`. Under module federation — the
 *   usual way an MFE loads — a bare `window` can resolve to the webpack Module
 *   instance rather than the real Window, and the read silently returns
 *   undefined.
 *
 * This is a snapshot written once by the server. It never updates, so there is
 * nothing to subscribe to and no need to poll.
 */

// Kept module-private so this file has no internal imports beyond the feature
// name enum, which keeps it cheap to consume from outside the package.
const getGlobalScope = (): Window => globalThis as unknown as Window;

export const getStormConfigFlags = ():
  | Record<string, boolean | undefined>
  | undefined => getGlobalScope().__StormConfig__?.flags;

/**
 * True only when the server explicitly emitted the flag as enabled. A missing
 * global, a missing flag, or a server that never emitted the script all read as
 * false — the flag is off unless someone said otherwise.
 */
export const isStormConfigFeatureEnabled = (
  featureName: WeblabedFeatures | string,
): boolean => getStormConfigFlags()?.[featureName] === true;

/**
 * Distinguishes "the server never emitted this" from "the flag is off", for
 * callers that need to fall back to the page context rather than assume off.
 */
export const isStormConfigAvailable = (): boolean =>
  getStormConfigFlags() !== undefined;

/**
 * Reads the per-application dark-mode setting from `window.__StormConfig__.theme`.
 *
 * Returns the raw tri-state on purpose:
 * - `true` / `false` when the server emitted a value for this application, so
 *   callers can read it synchronously at first render (no post-hydration flip).
 * - `undefined` when the server never emitted the theme block (older server, or
 *   the script is missing), so a caller can fall back to the async
 *   `window.__AACChrome__.context.isDarkModeEnabled` with `??`.
 *
 * Dark mode is a per-application property, not a weblab flag, so it lives under
 * `theme` rather than in `flags`.
 */
export const isStormConfigDarkModeEnabled = (): boolean | undefined =>
  getGlobalScope().__StormConfig__?.theme?.isDarkModeEnabled;
